import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  TextField,
  Button,
  Badge,
  DataTable,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Modal,
  Box,
  Divider,
  SkeletonBodyText,
  Checkbox,
  Spinner,
  Icon,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { warehouseApi, assetsApi, rentalsApi } from '@/services';
import type { Rental, Asset, RentalItem, RateQuote, LabelResponse } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

interface SerialAssignment {
  rentalItemId: string;
  serialNumbers: string[];
}

export default function WarehouseDispatch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState('');
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [assignments, setAssignments] = useState<Map<string, string[]>>(new Map());
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetTotal, setAssetTotal] = useState(0);
  const [trackingNumber, setTrackingNumber] = useState('');

  // Shipping state
  const [manualMode, setManualMode] = useState(false);
  const [rates, setRates] = useState<RateQuote[]>([]);
  const [selectedRate, setSelectedRate] = useState<RateQuote | null>(null);

  // Rates belong to ONE rental — never let rental A's quoted service generate
  // a label for rental B (Codex finding #4).
  const selectedRentalId = selectedRental?.id;
  useEffect(() => {
    setRates([]);
    setSelectedRate(null);
    setGeneratedLabel(null);
  }, [selectedRentalId]);
  const [generatedLabel, setGeneratedLabel] = useState<{
    outbound: LabelResponse;
    returnLabel: LabelResponse | null;
    returnLabelError?: string;
  } | null>(null);

  // Scanner support refs
  const scanContainerRef = useRef<HTMLDivElement>(null);
  const barcodeBuffer = useRef('');
  const barcodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to focus the scan input inside the container
  const focusScanInput = useCallback(() => {
    const input = scanContainerRef.current?.querySelector('input');
    if (input) input.focus();
  }, []);

  // Load rental if ID provided
  const { data: rental, isLoading: rentalLoading } = useQuery({
    queryKey: ['rental', id],
    queryFn: () => rentalsApi.getById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (rental) {
      setSelectedRental(rental);
      const existing = new Map<string, string[]>();
      rental.items.forEach((item) => {
        if (item.assignedSerials.length > 0) {
          existing.set(item.id, [...item.assignedSerials]);
        }
      });
      setAssignments(existing);
    }
  }, [rental]);

  // Scan mutation
  const scanMutation = useMutation({
    mutationFn: (value: string) => warehouseApi.scan(value),
    onSuccess: (result) => {
      setSelectedRental(result.rental);
      setScanError('');
      setScanValue('');
      const existing = new Map<string, string[]>();
      result.rental.items.forEach((item: RentalItem) => {
        if (item.assignedSerials.length > 0) {
          existing.set(item.id, [...item.assignedSerials]);
        }
      });
      setAssignments(existing);
    },
    onError: () => {
      setScanError('No order found with this tracking number or serial');
      barcodeBuffer.current = '';
    },
  });

  /**
   * Global keyboard listener for barcode scanner support.
   * Hardware barcode scanners "type" characters very fast (< 50ms between keys)
   * and end with Enter. This listener captures that input even when the text
   * field isn't focused, buffers the characters, and auto-submits on Enter.
   */
  useEffect(() => {
    // Only listen when on the scan screen (no rental selected, no URL id)
    if (id || selectedRental) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in a modal or other input
      const tag = (e.target as HTMLElement)?.tagName;
      const isInScanInput = scanContainerRef.current?.contains(e.target as Node);
      if (tag === 'TEXTAREA' || (tag === 'INPUT' && !isInScanInput)) return;

      if (e.key === 'Enter') {
        // Submit whatever is in the buffer or the text field
        const value = barcodeBuffer.current || scanValue;
        if (value.trim()) {
          setScanValue(value.trim());
          setTimeout(() => focusScanInput(), 0);
          barcodeBuffer.current = '';
          scanMutation.mutate(value.trim());
        }
        e.preventDefault();
        return;
      }

      // Only capture printable characters (barcode data)
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        barcodeBuffer.current += e.key;
        setScanValue(barcodeBuffer.current);
        focusScanInput();

        // Reset buffer after 500ms of no input (scanner is done)
        if (barcodeTimer.current) clearTimeout(barcodeTimer.current);
        barcodeTimer.current = setTimeout(() => {
          barcodeBuffer.current = '';
        }, 500);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      if (barcodeTimer.current) clearTimeout(barcodeTimer.current);
    };
  }, [id, selectedRental, scanValue, scanMutation, focusScanInput]);

  // Auto-focus scan input on mount and when returning to scan mode
  useEffect(() => {
    if (!id && !selectedRental) {
      const timer = setTimeout(() => focusScanInput(), 100);
      return () => clearTimeout(timer);
    }
  }, [id, selectedRental, focusScanInput]);

  // Assign serials mutation
  const assignMutation = useMutation({
    mutationFn: (data: { rentalId: string; assignments: SerialAssignment[] }) =>
      warehouseApi.assignSerials(data.rentalId, { assignments: data.assignments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental', selectedRental?.id] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-queue'] });
    },
  });

  // Get rates mutation
  const ratesMutation = useMutation({
    mutationFn: (rentalId: string) => warehouseApi.getRatesForRental(rentalId),
    onSuccess: (data) => {
      setRates(data);
      // Default to the cheapest service so staff can print the label with a
      // single click; the list stays visible for the odd manual override.
      const cheapest = [...data].sort((a, b) => a.totalCost - b.totalCost)[0] ?? null;
      setSelectedRate(cheapest);
      setGeneratedLabel(null);
    },
  });

  // Generate label mutation
  const labelMutation = useMutation({
    mutationFn: (data: { rentalId: string; carrierCode?: string; serviceCode?: string; provider?: string }) =>
      warehouseApi.generateLabel(data.rentalId, {
        carrierCode: data.carrierCode,
        serviceCode: data.serviceCode,
        provider: data.provider,
      }),
    onSuccess: (data) => {
      setGeneratedLabel({
        outbound: data.outbound,
        returnLabel: data.returnLabel,
        returnLabelError: data.returnLabelError,
      });
      setTrackingNumber(data.outbound.trackingNumber);
    },
  });

  // Ship mutation
  const shipMutation = useMutation({
    mutationFn: (data: { rentalId: string; trackingNumber?: string }) =>
      warehouseApi.markShipped(data.rentalId, data.trackingNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-queue'] });
      navigate('/warehouse');
    },
  });

  const handleScan = useCallback(() => {
    if (scanValue.trim()) {
      scanMutation.mutate(scanValue.trim());
    }
  }, [scanValue, scanMutation]);

  const handleScanKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleScan();
      }
    },
    [handleScan]
  );

  const handleSelectAsset = useCallback(async (itemId: string) => {
    if (!selectedRental) return;
    const item = selectedRental.items.find((i) => i.id === itemId);
    if (!item) return;

    setCurrentItemId(itemId);
    setAssetSearch('');
    try {
      const res = await assetsApi.searchAvailable(item.productSku);
      setAvailableAssets(res.data);
      setAssetTotal(res.total);
      setShowAssetModal(true);
    } catch {
      setScanError('Failed to load available assets');
    }
  }, [selectedRental]);

  // Re-query as the operator types or scans into the modal's search box.
  useEffect(() => {
    if (!showAssetModal || !currentItemId || !selectedRental) return;
    const item = selectedRental.items.find((i) => i.id === currentItemId);
    if (!item) return;
    const t = setTimeout(async () => {
      try {
        const res = await assetsApi.searchAvailable(item.productSku, assetSearch);
        setAvailableAssets(res.data);
        setAssetTotal(res.total);
      } catch {
        /* leave the previous list in place rather than blanking the modal */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [assetSearch, showAssetModal, currentItemId, selectedRental]);

  const handleAssignSerial = useCallback((serial: string) => {
    if (!currentItemId || !selectedRental) return;

    const item = selectedRental.items.find((i) => i.id === currentItemId);
    if (!item) return;

    const currentSerials = assignments.get(currentItemId) || [];
    const requiredCount = item.quantity;

    if (currentSerials.length >= requiredCount) {
      return;
    }

    const newAssignments = new Map(assignments);
    newAssignments.set(currentItemId, [...currentSerials, serial]);
    setAssignments(newAssignments);
    setShowAssetModal(false);
    setCurrentItemId(null);
  }, [currentItemId, selectedRental, assignments]);

  const handleRemoveSerial = useCallback((itemId: string, serial: string) => {
    const currentSerials = assignments.get(itemId) || [];
    const newAssignments = new Map(assignments);
    newAssignments.set(
      itemId,
      currentSerials.filter((s) => s !== serial)
    );
    setAssignments(newAssignments);
  }, [assignments]);

  const handleSaveAssignments = useCallback(async () => {
    if (!selectedRental) return;

    const assignmentList: SerialAssignment[] = [];
    assignments.forEach((serials, itemId) => {
      assignmentList.push({ rentalItemId: itemId, serialNumbers: serials });
    });

    await assignMutation.mutateAsync({
      rentalId: selectedRental.id,
      assignments: assignmentList,
    });
  }, [selectedRental, assignments, assignMutation]);

  const handleGetRates = useCallback(() => {
    if (!selectedRental) return;
    ratesMutation.mutate(selectedRental.id);
  }, [selectedRental, ratesMutation]);

  const handleGenerateLabel = useCallback(() => {
    if (!selectedRental || !selectedRate) return;
    labelMutation.mutate({
      rentalId: selectedRental.id,
      carrierCode: selectedRate.carrierCode,
      serviceCode: selectedRate.serviceCode,
      provider: selectedRate.provider,
    });
  }, [selectedRental, selectedRate, labelMutation]);

  const openLabelInNewTab = useCallback((label: LabelResponse) => {
    if (!label?.labelImageBase64) return;
    const format = label.labelFormat?.toLowerCase() || 'pdf';
    const mimeType =
      format === 'pdf' ? 'application/pdf' :
      format === 'png' ? 'image/png' :
      format === 'gif' ? 'image/gif' :
      'application/pdf';

    // Embedded Shopify apps run in a sandboxed iframe that often blocks data
    // URLs in window.open. Convert to a Blob + object URL — these are allowed.
    const byteString = atob(label.labelImageBase64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      alert('Popup blocked — please allow popups for this site to preview the shipping label.');
    }
    // Revoke the URL after the new tab has had a chance to load it
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, []);

  const handlePrintLabel = useCallback(() => {
    if (generatedLabel?.outbound) openLabelInNewTab(generatedLabel.outbound);
  }, [generatedLabel, openLabelInNewTab]);

  const handlePrintReturnLabel = useCallback(() => {
    if (generatedLabel?.returnLabel) openLabelInNewTab(generatedLabel.returnLabel);
  }, [generatedLabel, openLabelInNewTab]);

  const handleShip = useCallback(async () => {
    if (!selectedRental) return;
    await shipMutation.mutateAsync({
      rentalId: selectedRental.id,
      trackingNumber: trackingNumber || undefined,
    });
  }, [selectedRental, trackingNumber, shipMutation]);

  // Only equipment lines require serial assignment; wrap/add-on lines are
  // packed but not asset-tracked, so they never gate ship-readiness.
  const isEquipmentItem = (item: RentalItem) =>
    (item.itemType ?? 'equipment') === 'equipment';

  const isFullyAssigned = selectedRental?.items
    .filter(isEquipmentItem)
    .every((item) => {
      const assigned = assignments.get(item.id) || [];
      return assigned.length >= item.quantity;
    });

  // A possible duplicate is held until an admin clears or cancels it; the server
  // refuses these actions too, this just stops the operator hitting the error.
  const duplicateBlocked =
    !!selectedRental?.duplicateOfRentalId && !selectedRental?.duplicateClearedAt;
  const duplicateReason = `Possible duplicate of order ${
    selectedRental?.duplicateOfOrderNumber || 'on file'
  } — an admin must clear or cancel it before dispatch`;

  const canShip = isFullyAssigned && !duplicateBlocked &&
    (selectedRental?.rentalStatus === 'processing' || selectedRental?.rentalStatus === 'ready_to_ship');

  // Handler to reset and go back to scan mode
  const handleScanAnother = useCallback(() => {
    setSelectedRental(null);
    setAssignments(new Map());
    setScanValue('');
    setScanError('');
    setTrackingNumber('');
    setRates([]);
    setSelectedRate(null);
    setGeneratedLabel(null);
    barcodeBuffer.current = '';
    // Focus scan input after state resets
    setTimeout(() => focusScanInput(), 100);
  }, []);

  // Render scan interface if no rental selected
  if (!id && !selectedRental) {
    return (
      <Page
        title="Dispatch Order"
        backAction={{ content: 'Back', onAction: () => navigate('/warehouse') }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="200" blockAlign="center">
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#008060',
                      animation: 'pulse 2s infinite',
                    }}
                  />
                  <Text as="h2" variant="headingMd">
                    Ready to Scan
                  </Text>
                </InlineStack>
                <Text as="p" tone="subdued">
                  Scan a barcode with your scanner, or type an order number / tracking number and press Enter.
                </Text>
                <InlineStack gap="200">
                  <div style={{ flex: 1 }} ref={scanContainerRef} onKeyDown={handleScanKeyDown}>
                    <TextField
                      label="Scan or enter tracking/order number"
                      labelHidden
                      placeholder="Scan barcode or type order number..."
                      value={scanValue}
                      onChange={(val) => {
                        setScanValue(val);
                        setScanError('');
                        barcodeBuffer.current = val;
                      }}
                      autoComplete="off"
                      autoFocus
                      focused
                      error={scanError}
                      prefix={<Icon source={SearchIcon} />}
                      connectedRight={undefined}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleScan}
                    loading={scanMutation.isPending}
                  >
                    Search
                  </Button>
                </InlineStack>
                {scanMutation.isPending && (
                  <InlineStack gap="200" blockAlign="center">
                    <Spinner size="small" />
                    <Text as="p" tone="subdued">Looking up order...</Text>
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Scanner Tips</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Your barcode scanner should be in keyboard mode (HID). Just point and scan — the barcode will appear in the field above and automatically search.
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Supported formats: Order number (HF-1234), tracking number, or serial number.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </Page>
    );
  }

  if (rentalLoading) {
    return (
      <Page title="Dispatch Order">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (!selectedRental) {
    return (
      <Page title="Dispatch Order">
        <Card>
          <Banner tone="warning">Order not found</Banner>
        </Card>
      </Page>
    );
  }

  const itemRows = selectedRental.items.filter(isEquipmentItem).map((item) => {
    const assignedSerials = assignments.get(item.id) || [];
    const requiredCount = item.quantity;
    const isComplete = assignedSerials.length >= requiredCount;

    return [
      item.productName,
      item.productSku,
      String(item.quantity),
      item.isBilateral ? 'Yes' : 'No',
      <BlockStack key={item.id} gap="100">
        {assignedSerials.map((serial) => (
          <InlineStack key={serial} gap="100" blockAlign="center">
            <Badge tone="success">{serial}</Badge>
            <Button
              variant="plain"
              tone="critical"
              onClick={() => handleRemoveSerial(item.id, serial)}
            >
              Remove
            </Button>
          </InlineStack>
        ))}
        {!isComplete && (
          <Button size="slim" onClick={() => handleSelectAsset(item.id)} disabled={duplicateBlocked}>
            {`Add Serial (${assignedSerials.length}/${requiredCount})`}
          </Button>
        )}
      </BlockStack>,
      isComplete ? (
        <Badge tone="success">Complete</Badge>
      ) : (
        <Badge tone="warning">{`${assignedSerials.length}/${requiredCount}`}</Badge>
      ),
    ];
  });

  // Non-equipment lines the packer must include in the box but that need no
  // serial assignment (wraps, bilateral fee, add-ons).
  const addonItemRows = selectedRental.items
    .filter((item) => !isEquipmentItem(item))
    .map((item) => [
      item.productName,
      item.productSku,
      String(item.quantity),
    ]);

  // Rates sorted cheapest-first for a vertical pick list (a DataTable here
  // overflowed into horizontal scrolling and hid the Select column).
  const sortedRates = [...rates].sort((a, b) => a.totalCost - b.totalCost);

  return (
    <Page
      title={`Order ${orderNumber(selectedRental)}`}
      backAction={{ content: 'Back', onAction: () => navigate('/warehouse') }}
      primaryAction={
        canShip
          ? {
              content: 'Mark as Shipped',
              onAction: handleShip,
              loading: shipMutation.isPending,
            }
          : undefined
      }
      secondaryActions={[
        ...(!id ? [{
          content: 'Scan Another',
          onAction: handleScanAnother,
        }] : []),
      ]}
    >
      <Layout>
        {duplicateBlocked && (
          <Layout.Section>
            <Banner tone="critical" title="Possible duplicate — held">
              <BlockStack gap="200">
                <p>{duplicateReason}.</p>
                <InlineStack>
                  <Button variant="plain" onClick={() => navigate(`/rentals/${selectedRental.id}`)}>
                    Review this order
                  </Button>
                </InlineStack>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}
        {/* Order Summary */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Order Details
              </Text>
              <Divider />
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Customer</Text>
                  <Text as="span" fontWeight="semibold">
                    {selectedRental.clinic?.name || selectedRental.patientName || 'N/A'}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Start Date</Text>
                  <Text as="span">
                    {selectedRental.rentalStartDate
                      ? new Date(selectedRental.rentalStartDate).toLocaleDateString()
                      : 'N/A'}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Duration</Text>
                  <Text as="span">{selectedRental.rentalWeeks} weeks</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Status</Text>
                  <Badge>{selectedRental.rentalStatus}</Badge>
                </InlineStack>
                <Divider />
                <Text as="h3" variant="headingSm">Ship to</Text>
                {(() => {
                  const r = selectedRental as any;
                  const contactName = r.patient
                    ? `${r.patient.firstName ?? ''} ${r.patient.lastName ?? ''}`.trim()
                    : r.customerName || r.patientName || '';
                  const contactPhone = r.patient?.phone || r.customerPhone;
                  const contactEmail = r.patient?.email || r.customerEmail;
                  // Mirror warehouse.service buildShipToAddress exactly:
                  // custom → patient (falling back to clinic when empty) →
                  // clinic.shippingAddress JSON (address1/city/province/zip).
                  const clinicAddr = r.clinic?.shippingAddress
                    ? {
                        address1: r.clinic.shippingAddress.address1,
                        address2: r.clinic.shippingAddress.address2,
                        city: r.clinic.shippingAddress.city,
                        province:
                          r.clinic.shippingAddress.province ??
                          r.clinic.shippingAddress.provinceCode,
                        postalCode: r.clinic.shippingAddress.zip,
                      }
                    : null;
                  const patientAddr = r.patient?.streetAddress
                    ? {
                        address1: r.patient.streetAddress,
                        address2: undefined as string | undefined,
                        city: r.patient.city,
                        province: r.patient.province,
                        postalCode: r.patient.postalCode,
                      }
                    : null;
                  const addr =
                    r.shippingAddressType === 'custom' && r.customShippingAddress
                      ? {
                          address1:
                            r.customShippingAddress.address1 ?? r.customShippingAddress.street,
                          address2: r.customShippingAddress.address2,
                          city: r.customShippingAddress.city,
                          province: r.customShippingAddress.province,
                          postalCode: r.customShippingAddress.postalCode,
                        }
                      : r.shippingAddressType === 'patient'
                        ? (patientAddr ?? clinicAddr)
                        : (clinicAddr ?? patientAddr);
                  return (
                    <BlockStack gap="100">
                      {contactName && <Text as="p" fontWeight="semibold">{contactName}</Text>}
                      {addr && (addr.address1 || addr.city) ? (
                        <Text as="p">
                          {[addr.address1, addr.address2].filter(Boolean).join(', ')}
                          {addr.address1 || addr.address2 ? <br /> : null}
                          {[addr.city, addr.province, addr.postalCode].filter(Boolean).join(', ')}
                        </Text>
                      ) : (
                        <Text as="p" tone="critical">
                          No shipping address on file — rates and labels will fail. Set the
                          address on the order before dispatching.
                        </Text>
                      )}
                      {contactPhone && <Text as="p" tone="subdued" variant="bodySm">{contactPhone}</Text>}
                      {contactEmail && <Text as="p" tone="subdued" variant="bodySm">{contactEmail}</Text>}
                    </BlockStack>
                  );
                })()}
                {selectedRental.notes && (
                  <>
                    <Divider />
                    <Text as="h3" variant="headingSm">Order notes</Text>
                    <Text as="p">{selectedRental.notes}</Text>
                  </>
                )}
              </BlockStack>
            </BlockStack>
          </Card>

          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Shipping
                </Text>

                {/* Label generation success */}
                {generatedLabel && (
                  <Banner tone="success">
                    <BlockStack gap="200">
                      <Text as="p" fontWeight="semibold">
                        Outbound label: {generatedLabel.outbound.trackingNumber}
                      </Text>
                      {generatedLabel.outbound.shipmentCost && (
                        <Text as="p">
                          Cost: ${generatedLabel.outbound.shipmentCost.total} {generatedLabel.outbound.shipmentCost.currency}
                        </Text>
                      )}
                      {generatedLabel.returnLabel && (
                        <Text as="p" fontWeight="semibold">
                          Return label: {generatedLabel.returnLabel.trackingNumber}
                        </Text>
                      )}
                      {generatedLabel.returnLabelError && (
                        <Text as="p" tone="critical">
                          Return label failed: {generatedLabel.returnLabelError}. You can regenerate it later from the rental detail page.
                        </Text>
                      )}
                      <InlineStack gap="200">
                        <Button onClick={handlePrintLabel}>
                          Print Outbound Label
                        </Button>
                        {generatedLabel.returnLabel && (
                          <Button onClick={handlePrintReturnLabel}>
                            Print Return Label
                          </Button>
                        )}
                      </InlineStack>
                    </BlockStack>
                  </Banner>
                )}

                {/* Toggle for manual mode */}
                <Checkbox
                  label="Enter tracking manually"
                  checked={manualMode}
                  onChange={setManualMode}
                />

                {manualMode ? (
                  <TextField
                    label="Tracking Number"
                    value={trackingNumber}
                    onChange={setTrackingNumber}
                    placeholder="Enter tracking number..."
                    autoComplete="off"
                  />
                ) : (
                  <BlockStack gap="300">
                    {/* Get rates button */}
                    {rates.length === 0 && !generatedLabel && (
                      <Button
                        onClick={handleGetRates}
                        loading={ratesMutation.isPending}
                        disabled={!canShip}
                      >
                        Get Shipping Rates
                      </Button>
                    )}

                    {ratesMutation.isPending && (
                      <InlineStack gap="200" blockAlign="center">
                        <Spinner size="small" />
                        <Text as="p" tone="subdued">Fetching rates...</Text>
                      </InlineStack>
                    )}

                    {ratesMutation.isError && (
                      <Banner tone="warning">
                        Failed to get rates. You can enter tracking manually.
                      </Banner>
                    )}

                    {/* Rate selection table */}
                    {rates.length > 0 && !generatedLabel && (
                      <BlockStack gap="200">
                        <Text as="p" fontWeight="semibold">Select a shipping service:</Text>
                        <BlockStack gap="150">
                          {sortedRates.map((rate, idx) => {
                            const isSelected =
                              selectedRate?.provider === rate.provider &&
                              selectedRate?.carrierCode === rate.carrierCode &&
                              selectedRate?.serviceCode === rate.serviceCode;
                            return (
                              <InlineStack
                                key={`${rate.provider ?? ''}-${rate.carrierCode}-${rate.serviceCode}`}
                                align="space-between"
                                blockAlign="center"
                                wrap={false}
                                gap="200"
                              >
                                <BlockStack gap="050">
                                  <Text as="p">
                                    {rate.serviceName}
                                    {idx === 0 ? ' — cheapest' : ''}
                                  </Text>
                                  <Text as="p" tone="subdued" variant="bodySm">
                                    {rate.carrierName}
                                    {rate.estimatedDays != null
                                      ? ` · est. ${rate.estimatedDays} day${rate.estimatedDays !== 1 ? 's' : ''}`
                                      : ''}
                                  </Text>
                                </BlockStack>
                                <InlineStack gap="200" blockAlign="center" wrap={false}>
                                  <Text as="p" fontWeight="semibold">
                                    ${rate.totalCost.toFixed(2)}
                                  </Text>
                                  <Button
                                    size="slim"
                                    variant={isSelected ? 'primary' : undefined}
                                    onClick={() => setSelectedRate(rate)}
                                  >
                                    {isSelected ? 'Selected' : 'Select'}
                                  </Button>
                                </InlineStack>
                              </InlineStack>
                            );
                          })}
                        </BlockStack>

                        {labelMutation.isError && (
                          <Banner tone="critical" title="Label generation failed">
                            <p>
                              {(labelMutation.error as any)?.response?.data?.message ??
                                (labelMutation.error as Error)?.message ??
                                'Unknown error — check with support.'}
                            </p>
                          </Banner>
                        )}
                        {selectedRate && (
                          <Button
                            variant="primary"
                            onClick={handleGenerateLabel}
                            loading={labelMutation.isPending}
                            disabled={duplicateBlocked}
                          >
                            Generate Label ({selectedRate.carrierName} - {selectedRate.serviceName})
                          </Button>
                        )}
                      </BlockStack>
                    )}
                  </BlockStack>
                )}

                {selectedRental.shippingAddress && (
                  <BlockStack gap="100">
                    <Text as="p" fontWeight="semibold">Ship To:</Text>
                    <Text as="p">
                      {selectedRental.shippingAddress.address1}
                      {selectedRental.shippingAddress.address2 && (
                        <>, {selectedRental.shippingAddress.address2}</>
                      )}
                    </Text>
                    <Text as="p">
                      {selectedRental.shippingAddress.city},{' '}
                      {selectedRental.shippingAddress.province}{' '}
                      {selectedRental.shippingAddress.zip}
                    </Text>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </Box>
        </Layout.Section>

        {/* Items */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Equipment
                </Text>
                {isFullyAssigned ? (
                  <Badge tone="success">All Assigned</Badge>
                ) : (
                  <Badge tone="warning">Serials Needed</Badge>
                )}
              </InlineStack>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text']}
                headings={['Product', 'SKU', 'Qty', 'Bilateral', 'Assigned Serials', 'Status']}
                rows={itemRows}
              />
              {addonItemRows.length > 0 && (
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    Also pack (no serial needed)
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric']}
                    headings={['Item', 'SKU', 'Qty']}
                    rows={addonItemRows}
                  />
                </BlockStack>
              )}
              {/* Sits below the table, not in the page header: the operator should
                  read the assigned serials before committing them. */}
              <InlineStack align="end">
                <Button
                  variant="primary"
                  onClick={handleSaveAssignments}
                  loading={assignMutation.isPending}
                  disabled={assignments.size === 0 || duplicateBlocked}
                >
                  Save Assignments
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Asset Selection Modal */}
      <Modal
        open={showAssetModal}
        onClose={() => {
          setShowAssetModal(false);
          setAssetSearch('');
        }}
        title="Select Asset"
      >
        <Modal.Section>
          <BlockStack gap="300">
            {/* Every machine shares one product SKU, so this list runs to hundreds of
                rows. Searching server-side is the only way to reach a specific unit —
                and a scanned GS1 label is reduced to its serial, so scanning straight
                into this box finds the machine. */}
            <TextField
              label="Find a serial"
              labelHidden
              value={assetSearch}
              onChange={setAssetSearch}
              placeholder="Type or scan a serial / GPS id…"
              prefix={<Icon source={SearchIcon} tone="base" />}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setAssetSearch('')}
              helpText={
                assetTotal > availableAssets.length
                  ? `Showing ${availableAssets.length} of ${assetTotal} available — search to narrow.`
                  : `${availableAssets.length} available`
              }
            />
          {availableAssets.length === 0 ? (
            <Banner tone="warning">
              {assetSearch
                ? 'No available assets match that serial.'
                : 'No available assets for this product.'}
            </Banner>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'text']}
              headings={['Serial Number', 'Status', 'Action']}
              rows={availableAssets.map((asset) => [
                asset.serialNumber,
                <Badge key={asset.id} tone="success">{asset.assetStatus}</Badge>,
                <Button
                  key={`select-${asset.id}`}
                  size="slim"
                  onClick={() => handleAssignSerial(asset.serialNumber)}
                >
                  Select
                </Button>,
              ])}
            />
          )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
